import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  FormHelperText,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { EntryDetermination, FileDetail, ReviewStageData } from '@munifocus/contracts-gov';
import { ENTRY_DETERMINATIONS } from '@munifocus/contracts-gov';
import { PreVisitContactLog } from '../../components/PreVisitContactLog';
import { SnippetPicker, useCaretInsert } from '../../components/SnippetPicker';
import { StageLayout } from '../../components/StageLayout';
import { useSaveStage } from '../../lib/files-api';
import { useSubmitValidation } from '../../lib/useSubmitValidation';

/** The pre-visit contact options, by their stored value. */
const CONTACT_LABEL_KEY: Record<'phone' | 'letter' | 'none', string> = {
  phone: 'gov.review.contactPhone',
  letter: 'gov.review.contactLetter',
  none: 'gov.review.contactNone',
};

function dwellingLabel(value: 'yes' | 'no' | 'unknown', t: (key: string) => string): string {
  if (value === 'yes') return t('common.yes');
  if (value === 'no') return t('common.no');
  return t('gov.review.unknown');
}

export function ReviewStage({ file }: { file: FileDetail }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const save = useSaveStage(file.id, 'review');
  const initial = (file.stageData?.review ?? {}) as ReviewStageData;

  const [isDwelling, setIsDwelling] = useState<'yes' | 'no' | 'unknown'>(
    initial.isDwelling ?? 'unknown',
  );
  const [entryDetermination, setEntryDetermination] = useState<EntryDetermination | ''>(
    initial.entryDetermination ?? '',
  );
  const [preVisitContact, setPreVisitContact] = useState<'phone' | 'letter' | 'none'>(
    initial.preVisitContact ?? 'phone',
  );
  const [notes, setNotes] = useState(initial.notes ?? '');
  const notesCaret = useCaretInsert();

  // Free text recorded before this file used the statutory list. Shown read-only
  // rather than migrated: no automatic mapping could turn officer prose into one
  // of the six codes without guessing, and this is an evidentiary record. Saved
  // back untouched so the original wording survives the officer choosing a code.
  const priorEntryAuthority = initial.entryAuthority ?? '';

  // Driven by the Stage 1 property type — per the v46 flowchart this replaced
  // the old stream-branch decision. Public land needs no private-property entry
  // authority, so asking for one there is noise that invites a wrong answer.
  // Resolved by the API from the property type's `requiresEntryAuthority` flag,
  // rather than re-derived here — the list is administrable, so a client-side
  // copy of the rule would go stale the moment a category is added.
  const showEntryAccess = file.entryAccessApplies;
  // Private property needs the notice/consent determination before anyone goes
  // on site — the same rule the mobile review and the API's mobile route hold.
  // Completing without one sent the officer to the site visit with no recorded
  // authority to enter.
  const v = useSubmitValidation({
    entryDetermination:
      !showEntryAccess || entryDetermination !== '' ? null : t('gov.stage2.determination.required'),
  });

  const onComplete = () =>
    save.mutate(
      {
        isDwelling,
        // Omitted rather than sent empty: the DTO validates against the six
        // codes, so `''` would be rejected on a file the officer has not yet
        // determined.
        ...(entryDetermination ? { entryDetermination } : {}),
        entryAuthority: priorEntryAuthority,
        preVisitContact,
        notes,
        advance: true,
      },
      { onSuccess: () => navigate(`/files/${file.id}`) },
    );

  // What this stage recorded, for the read-only view of a stage the file is no
  // longer at. Same values, stated rather than asked.
  const record = [
    ...(showEntryAccess
      ? [
          { label: t('gov.review.dwelling'), value: dwellingLabel(isDwelling, t) },
          {
            label: t('gov.stage2.determination.label'),
            value: entryDetermination
              ? t(`gov.stage2.determination.options.${entryDetermination}`)
              : t('gov.stage2.determination.empty'),
          },
          // Only surfaced on files that carry it, so the record does not grow an
          // empty row for every file created since the statutory list landed.
          ...(priorEntryAuthority
            ? [{ label: t('gov.stage2.determination.priorLabel'), value: priorEntryAuthority }]
            : []),
        ]
      : [
          {
            label: t('gov.stage2.determination.label'),
            value: t('gov.review.entryAccessSuppressed'),
          },
        ]),
    { label: t('gov.review.preVisitContact'), value: t(CONTACT_LABEL_KEY[preVisitContact]) },
    { label: t('gov.stage.notes'), value: notes, block: true },
  ];

  return (
    <StageLayout
      file={file}
      stageId="review"
      onComplete={v.guard(onComplete)}
      saving={save.isPending}
      record={record}
    >
      {save.isError && <Alert severity="error">{t('gov.stage.saveError')}</Alert>}

      {showEntryAccess ? (
        <>
          <FormControl fullWidth size="small">
            <InputLabel>{t('gov.review.dwelling')}</InputLabel>
            <Select
              label={t('gov.review.dwelling')}
              aria-label={t('gov.review.dwelling')}
              value={isDwelling}
              onChange={(e) => setIsDwelling(e.target.value as typeof isDwelling)}
            >
              <MenuItem value="yes">{t('common.yes')}</MenuItem>
              <MenuItem value="no">{t('common.no')}</MenuItem>
              <MenuItem value="unknown">{t('gov.review.unknown')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" error={Boolean(v.errors.entryDetermination)}>
            <InputLabel>{t('gov.stage2.determination.label')}</InputLabel>
            <Select
              label={t('gov.stage2.determination.label')}
              aria-label={t('gov.stage2.determination.label')}
              inputRef={v.register('entryDetermination')}
              value={entryDetermination}
              onChange={(e) => setEntryDetermination(e.target.value as EntryDetermination)}
            >
              {ENTRY_DETERMINATIONS.map((d) => (
                <MenuItem key={d} value={d}>
                  {/* The statutory text sits under each option: officers pick
                      these rarely and the subsection alone does not say what it
                      authorizes. Stacked, not inline, because the French runs
                      longer than the English on every one of the six. */}
                  <Stack spacing={0.25} sx={{ py: 0.5, whiteSpace: 'normal' }}>
                    <Typography variant="body2">
                      {t(`gov.stage2.determination.options.${d}`)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t(`gov.stage2.determination.descriptions.${d}`)}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
            {v.errors.entryDetermination && (
              <FormHelperText>{v.errors.entryDetermination}</FormHelperText>
            )}
          </FormControl>

          <Typography variant="caption" color="text.secondary">
            {t('gov.stage2.determination.hint')}
          </Typography>

          {/* A warrant is not a route onto the property yet — it is an
              application. Saying so here prevents the officer reading the
              selection as permission to attend. */}
          {entryDetermination === 'warrant_144_10' && (
            <Alert severity="info">{t('gov.stage2.determination.warrantNotice')}</Alert>
          )}

          {priorEntryAuthority && (
            <TextField
              fullWidth
              size="small"
              disabled
              label={t('gov.stage2.determination.priorLabel')}
              helperText={t('gov.stage2.determination.priorHint')}
              value={priorEntryAuthority}
            />
          )}
        </>
      ) : (
        <Alert severity="info">
          {t('gov.review.entryAccessSuppressed')} — {t('gov.review.entryAccessSuppressedHint')}
        </Alert>
      )}

      <FormControl fullWidth size="small">
        <InputLabel>{t('gov.review.preVisitContact')}</InputLabel>
        <Select
          label={t('gov.review.preVisitContact')}
          aria-label={t('gov.review.preVisitContact')}
          value={preVisitContact}
          onChange={(e) => setPreVisitContact(e.target.value as typeof preVisitContact)}
        >
          <MenuItem value="phone">{t('gov.review.contactPhone')}</MenuItem>
          <MenuItem value="letter">{t('gov.review.contactLetter')}</MenuItem>
          <MenuItem value="none">{t('gov.review.contactNone')}</MenuItem>
        </Select>
      </FormControl>

      {/* The log is the record of what was actually attempted; the select above
          is the officer's plan. They are deliberately not derived from each
          other — a letter that came back undelivered does not retract the
          decision to send one. */}
      <PreVisitContactLog fileId={file.id} />

      <Stack spacing={0.5}>
        <TextField
          fullWidth
          multiline
          minRows={3}
          label={t('gov.stage.notes')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          {...notesCaret.trackProps}
        />
        <Box>
          <SnippetPicker
            context="note"
            onInsert={(text) => setNotes((current) => notesCaret.insertInto(current, text))}
          />
        </Box>
      </Stack>
    </StageLayout>
  );
}
